/**
 *
 *  Send Reviews Modal
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 05/07/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import moment from "moment-timezone";

import { useAppState } from "../../state";
import { useRegion } from "../../context/RegionProvider";

// Components
import RateSession from "../RateSession/RateSession";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils
import { getMomentWithTz } from "../../utils/date";
import { clientWidth, getPartnerPictureUrl } from "../../utils";
import { modalStyles } from "../../constants";

export const SendReviewsModal = ({ onClose }) => {
  const { theme } = useTheme();
  const { region } = useRegion();

  const {
    user,
    hasTriedToAutoLogin,
    bookingsWithoutRating,
    loadBookingsWithoutRating,
  } = useAppState();

  const customStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.newMobileDefault },
          {
            content: {
              ...modalStyles.newMobileDefault.content,
              height: "473px",
              minHeight: "436px",
              top: "auto",
            },
          }
        )
      : modalStyles.default;

  const [isModalOpen, setIsModalOpen] = useState();

  const modalResolveRef = React.useRef();

  useEffect(() => {
    if (!hasTriedToAutoLogin) {
      return;
    }

    if (user && user.id) {
      loadBookingsWithoutRating(user.id, user.region);
    }
  }, [user, hasTriedToAutoLogin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (bookingsWithoutRating && bookingsWithoutRating.length) {
      openModal();
    }
  }, [bookingsWithoutRating, isModalOpen]);

  const openModal = () => {
    return new Promise((resolve) => {
      modalResolveRef.current = resolve;

      setIsModalOpen(true);
    });
  };

  const closeModal = () => {
    if (modalResolveRef.current) {
      modalResolveRef.current();
      modalResolveRef.current = null;

      onClose();
    }

    setIsModalOpen(false);
  };

  return (
    <Modal
      appElement={/*__CLIENT__ && */ document.getElementById("#app")}
      isOpen={isModalOpen}
      // onRequestClose={closeModal}
      style={customStyle}
      contentLabel="Reviews modal"
    >
      {(bookingsWithoutRating && bookingsWithoutRating.length) || true ? (
        <RateSession
          partnerId={
            bookingsWithoutRating &&
            bookingsWithoutRating.length &&
            bookingsWithoutRating[0].partner.id
          }
          partnerFullName={`${
            bookingsWithoutRating &&
            bookingsWithoutRating.length &&
            bookingsWithoutRating[0].partner.firstName
          } ${
            bookingsWithoutRating &&
            bookingsWithoutRating.length &&
            bookingsWithoutRating[0].partner.lastName
          }`}
          partnerProfilePictureUrl={
            bookingsWithoutRating &&
            bookingsWithoutRating.length &&
            getPartnerPictureUrl(bookingsWithoutRating[0].partner)
          }
          bookingId={
            bookingsWithoutRating &&
            bookingsWithoutRating.length &&
            bookingsWithoutRating[0].id
          }
          bookingDateTimeLabel={
            bookingsWithoutRating &&
            bookingsWithoutRating.length &&
            bookingsWithoutRating[0] &&
            getMomentWithTz(
              moment.utc(bookingsWithoutRating[0].start),
              region
            ).format("MMMM Do YYYY [at] h:mma")
          }
          onClose={closeModal}
          // onReviewSubmitted={handleReviewSubmitted}
        />
      ) : null}
    </Modal>
  );
};

SendReviewsModal.displayName = "SendReviewsModal";

SendReviewsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default SendReviewsModal;
